/**
 * /finance/collections — Collections Workbench
 * Customer-grouped open AR with aging buckets, contact info, oldest invoice
 * surfaced for one-click outreach. Read-only — call-log / promise-to-pay
 * model is a follow-up wave.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ShieldAlert } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitShell";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import {
  buildCollectionsRows,
  type CollectionsBucket,
} from "@/data/finance/financeWorkbenchSelectors";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}K`
  : `$${Math.round(n).toLocaleString()}`;

const BUCKET_LABEL: Record<CollectionsBucket, string> = {
  "current": "Current",
  "1-30": "1–30 days",
  "31-60": "31–60 days",
  "61-90": "61–90 days",
  "90+": "90+ days",
};

const BUCKET_TONE: Record<CollectionsBucket, string> = {
  "current": "text-muted-foreground",
  "1-30": "text-foreground",
  "31-60": "text-warning",
  "61-90": "text-warning font-medium",
  "90+": "text-destructive font-medium",
};

export default function CollectionsWorkbenchPage() {
  const { jobs } = useAppData();
  const fin$ = useFinancialPermissions();
  const [bucketFilter, setBucketFilter] = useState<CollectionsBucket | "all">("all");
  const [search, setSearch] = useState("");

  const allRows = useMemo(() => buildCollectionsRows(jobs), [jobs]);

  const rows = useMemo(() => {
    let r = allRows;
    if (bucketFilter !== "all") r = r.filter((x) => x.bucket === bucketFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      r = r.filter((x) => x.customerName.toLowerCase().includes(s) || x.branch.toLowerCase().includes(s));
    }
    return r;
  }, [allRows, bucketFilter, search]);

  const bucketTotals = useMemo(() => {
    const t: Record<CollectionsBucket, { count: number; amount: number }> = {
      "current": { count: 0, amount: 0 },
      "1-30": { count: 0, amount: 0 },
      "31-60": { count: 0, amount: 0 },
      "61-90": { count: 0, amount: 0 },
      "90+": { count: 0, amount: 0 },
    };
    for (const r of allRows) {
      t[r.bucket].count += 1;
      t[r.bucket].amount += r.openBalance;
    }
    return t;
  }, [allRows]);

  if (!fin$.viewFinancialSummary) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <ShieldAlert className="h-6 w-6 text-destructive mb-3" />
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
        </div>
      </CockpitPage>
    );
  }

  const totalOpen = allRows.reduce((s, r) => s + r.openBalance, 0);
  const totalOverdue = allRows.filter((r) => r.oldestDueDays > 0).reduce((s, r) => s + r.openBalance, 0);

  return (
    <CockpitPage>
      <header className="pb-3 border-b border-border/60">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Collections Workbench</h1>
        <p className="text-[12px] text-muted-foreground mt-1">
          {allRows.length} customer{allRows.length === 1 ? "" : "s"} with open AR · {fmtCompact(totalOpen)} total · {fmtCompact(totalOverdue)} past due
        </p>
      </header>

      <FinanceNavStrip />

      {/* Reconciliation source-of-truth banner */}
      <div className="text-[11px] border border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400 px-3 py-2 rounded-sm flex items-start gap-2">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          <strong>Operational view.</strong> AR balances derived from job financials for collections workflow. Bookkeepable AR balances and aging live in the
          {" "}<Link to="/accounting/ar" className="underline hover:text-amber-800 dark:hover:text-amber-300">General Ledger AR module</Link> — reconcile there before reporting.
        </span>
      </div>


      {/* Aging summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 border border-border/60 divide-x divide-y md:divide-y-0 divide-border/60 bg-background">
        {(["current", "1-30", "31-60", "61-90", "90+"] as const).map((b) => {
          const t = bucketTotals[b];
          const isActive = bucketFilter === b;
          return (
            <button
              key={b}
              onClick={() => setBucketFilter(isActive ? "all" : b)}
              className={`px-4 py-3 text-left transition-colors ${isActive ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}
            >
              <p className={`text-[10px] uppercase tracking-[0.12em] font-medium ${BUCKET_TONE[b]}`}>{BUCKET_LABEL[b]}</p>
              <p className="text-[20px] font-semibold tabular-nums mt-1">{fmtCompact(t.amount)}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{t.count} customer{t.count === 1 ? "" : "s"}</p>
            </button>
          );
        })}
      </div>

      {/* Filter strip */}
      <div className="flex items-center justify-between gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer or branch…"
          className="bg-background border border-border/60 px-3 py-1.5 text-[13px] w-72 focus:outline-none focus:ring-1 focus:ring-primary/40"
        />
        {bucketFilter !== "all" && (
          <button onClick={() => setBucketFilter("all")} className="text-[11px] text-primary hover:underline">
            Clear filter · Showing {BUCKET_LABEL[bucketFilter]}
          </button>
        )}
      </div>

      {/* Worklist */}
      <div className="bg-background border border-border/60">
        <div className="grid grid-cols-[1fr_180px_120px_120px_80px] gap-4 px-4 py-2.5 border-b border-border/60 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
          <span>Customer</span>
          <span>Contact</span>
          <span className="text-right">Open balance</span>
          <span className="text-right">Oldest</span>
          <span className="text-right">Jobs</span>
        </div>
        {rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">No customers match.</p>
        ) : (
          rows.map((r) => {
            const tone = BUCKET_TONE[r.bucket];
            return (
              <div key={r.customerName} className="grid grid-cols-[1fr_180px_120px_120px_80px] gap-4 px-4 py-3 border-b border-border/40 last:border-0 hover:bg-muted/20 items-center">
                <div className="min-w-0">
                  <Link to={`/customers/${encodeURIComponent(r.customerName)}`} className="text-[13px] font-medium text-foreground hover:underline truncate block">
                    {r.customerName}
                  </Link>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {r.branch}
                    {r.lastPaymentDate ? ` · last paid ${new Date(r.lastPaymentDate).toLocaleDateString()}` : " · no payments on file"}
                  </p>
                </div>
                <div className="text-[11px] text-muted-foreground space-y-0.5 min-w-0">
                  {r.phone && (
                    <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-primary truncate">
                      <Phone className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{r.phone}</span>
                    </a>
                  )}
                  {r.email && (
                    <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-primary truncate">
                      <Mail className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{r.email}</span>
                    </a>
                  )}
                  {!r.phone && !r.email && <span className="text-muted-foreground/60">No contact</span>}
                </div>
                <div className="text-right">
                  <p className="text-[13px] font-semibold tabular-nums">{fmt(r.openBalance)}</p>
                  <p className="text-[10px] text-muted-foreground">{r.openInvoices} invoice{r.openInvoices === 1 ? "" : "s"}</p>
                </div>
                <div className={`text-right text-[12px] tabular-nums ${tone}`}>
                  {r.oldestDueDays === 0 ? "current" : `${r.oldestDueDays}d`}
                  <p className="text-[10px] text-muted-foreground font-normal">{BUCKET_LABEL[r.bucket]}</p>
                </div>
                <div className="text-right text-[11px]">
                  {r.jobIds.slice(0, 1).map((jid) => (
                    <Link key={jid} to={`/jobs/${jid}`} className="text-primary hover:underline">{jid}</Link>
                  ))}
                  {r.jobIds.length > 1 && <p className="text-muted-foreground text-[10px]">+{r.jobIds.length - 1} more</p>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </CockpitPage>
  );
}
